import { CollectibleId_Schema } from "@/services/collectibles/collectiblesSchema";
import { CategoryId_Schema } from "@/services/collection/categories/categoriesSchema";
import { CollectionItemId_Schema, CollectionItemStatus_Schema } from "@/services/collection/item/collectionItemSchema";
import { ApiResponse_Image_Schema, ApiResponse_ImageNoId_Schema, ApiResponse_ImagesNoId_Schema } from "@/services/images";
import { MarketplaceItem_Schema } from "@/services/marketplace/item/marketplaceItemSchema";
import { AvatarUrl_Schema } from "@/services/user/userSchema";
import { Nickname_Schema } from "@/services/user/profile/profileSchema";
import { Rating_Schema } from "@/services/user/rating/ratingSchema";
import { UserId_Schema } from "@/services/user/userIdSchema";
import { NaturalNumber_Schema, WholeNumber_Schema } from "@/utilities/schema";
import { z } from "zod";

export const PublicCollectionItem_ListItem_Schema = z
  .object({
    id: CollectionItemId_Schema,
    catalogueItemParams: z
      .object({
        label: z.string(),
        value: z.string().or(z.number()),
      })
      .array(),
    categoryId: CategoryId_Schema,
    collectible: z
      .object({
        id: CollectibleId_Schema,
        name: z.string(),
      })
      .nullable(),
    collectorAvatar: AvatarUrl_Schema,
    collectorId: UserId_Schema,
    collectorItemsCount: WholeNumber_Schema,
    collectorNickname: Nickname_Schema,
    collectorRating: Rating_Schema,
    collectorRatingsCount: WholeNumber_Schema,
    images: ApiResponse_ImagesNoId_Schema,
    likedByCurrentUser: z.boolean().nullable(), // null for not-logged users
    likesCount: WholeNumber_Schema,
    marketPlaceItem: MarketplaceItem_Schema.nullable(),
    name: z.string(),
    publishedDate: z.string(), // 2025-12-31 format expected
    status: CollectionItemStatus_Schema,
    statusList: CollectionItemStatus_Schema.array(),
  })
  .transform(({ marketPlaceItem, ...rest }) => ({
    marketplaceItem: marketPlaceItem,
    ...rest,
  }));

// Alias for search results - same structure as list items
export const PublicCollectionItem_SearchListItem_Schema = PublicCollectionItem_ListItem_Schema;

// Alias for featured items - same structure as list items
export const PublicCollectionItem_FeaturedListItem_Schema = PublicCollectionItem_ListItem_Schema;

export const PublicCollectionItem_FromCollectible_Schema = z
  .object({
    user: z.object({
      id: UserId_Schema,
      nickname: Nickname_Schema,
      avatarUrl: z.url().nullable(),
      rating: Rating_Schema,
      ratingCount: WholeNumber_Schema,
      itemsCount: NaturalNumber_Schema, // This is total items count (not only given collectible)
    }),
    id: CollectionItemId_Schema,
    name: z.string(),
    images: ApiResponse_ImageNoId_Schema.array(),
    inCollectionCount: NaturalNumber_Schema,
  })
  .transform((response) => ({
    owner: response.user,
    id: response.id,
    name: response.name,
    quantity: response.inCollectionCount,
    images: response.images,
  }));

export const PublicCollectionItem_Basics_Schema = z
  .object({
    id: CollectionItemId_Schema,
    category: CategoryId_Schema,
    likesCount: WholeNumber_Schema,
    likedByCurrentUser: z.boolean(),
    images: ApiResponse_Image_Schema.array(),
  })
  .transform(({ category, ...rest }) => ({
    categoryId: category,
    ...rest,
  }));
